/**
 * Kaptan kasası ve Cash to Master metinleri (ADR 0093; ortağın `kasa.pdf` ve `kasa-ekran.jpeg`).
 * Ekran adları İngilizce (ortağın adları); Türkçe ikinci dil.
 */

export const cashEn = {
  title: 'Finance',
  sections: {
    accounts: {
      title: 'Cash Accounts',
      description:
        'The cash boxes of the vessels: each currency is a separate account with its own balance. The office sets up the cash box; the vessel keeps it.',
    },
    'ctm-requests': {
      title: 'CTM Requests',
      description:
        'Cash to Master: the master requests cash from the company; the office reviews, approves and arranges the payment; the master confirms receipt.',
    },
    'ctm-approvals': {
      title: 'CTM Approvals & Transfers',
      description:
        'Requests waiting for the office decision or for the payment / agent delivery to be arranged.',
    },
    'ctm-receipts': {
      title: 'CTM Receipt Confirmation',
      description:
        'Approved requests waiting for the master to confirm the cash actually received. Only the confirmation posts the cash to the account.',
    },
    'ctm-history': {
      title: 'CTM History',
      description: 'Every Cash to Master request of the vessel with its workflow.',
    },
    transactions: {
      title: 'Cash Transactions',
      description:
        'The vessel cash book: every cash in and out is a separate transaction; each currency is a separate cash account.',
    },
    balances: {
      title: 'Cash Balance by Currency',
      description: 'Opening, cash in, cash out and balance of each currency account.',
    },
    reconciliation: {
      title: 'Cash Reconciliation',
      description:
        'At month end or on change of master the physical cash is counted and compared with the cash book; the month is then locked.',
    },
    monthly: {
      title: 'Monthly Cash Report',
      description: 'Monthly cash statement of a currency account, ready to print.',
    },
    reports: {
      title: 'Finance Reports',
      description: 'Cash book, CTM register and monthly statement exports.',
    },
  },
  vessel: 'Vessel',
  month: 'Period',
  account: 'Account',
  accountName: 'Master Cash - {{currency}}',
  search: 'Search transaction…',
  all: 'All',
  closeWindow: 'Close window',
  cancelAction: 'Cancel',
  tableOf: '{{name}} table',
  password: 'Password',
  readOnlyOffice:
    'The cash account is kept on board by the master. In the office it is read-only; the office decides on CTM requests, arranges payments and reviews the counts.',
  panels: {
    accounts: 'Cash Accounts',
    vesselAccounts: 'Cash Box – {{vessel}}',
    fleet: 'Fleet Cash Overview',
    request: 'CTM Request',
    status: 'CTM Approval & Status',
    list: 'CTM Request List',
    summary: 'CTM Summary',
    transactions: 'Cash Account – Transactions',
    balances: 'Cash Balance by Currency',
    statement: 'Monthly Cash Statement ({{currency}}) – {{month}}',
    reconciliation: 'Month-end Status',
    records: 'Counts, Handovers & Takeovers',
    reports: 'Finance Reports',
  },
  tabs: { workflow: 'Workflow', history: 'History' },
  ctm: {
    fields: {
      vessel: 'Vessel',
      ctmNo: 'CTM Reference No.',
      requestDate: 'Request Date',
      requiredDate: 'Required Date',
      port: 'Port / Country',
      currency: 'Currency',
      requestedAmount: 'Requested Amount',
      cashBalance: 'Current Cash Balance',
      estimatedExpenses: 'Estimated Expenses',
      purpose: 'Purpose',
      deliveryMethod: 'Delivery Method',
      remarks: 'Remarks / Justification',
      requestedBy: 'Requested By',
      status: 'Approval Status',
      auto: 'Assigned on save',
      remarksPlaceholder: 'Enter cash requirements and justification…',
    },
    purposes: {
      crew_cash_advance: 'Crew Cash Advance',
      crew_wages: 'Crew Wages',
      port_expenses: 'Port Expenses',
      provisions: 'Provisions',
      bonded_stores: 'Bonded Stores',
      medical_expenses: 'Medical Expenses',
      transportation: 'Transportation',
      repairs: 'Repairs',
      emergency_expenses: 'Emergency Expenses',
      other: 'Other',
    },
    methods: { agent: 'Agent', bank: 'Bank', other: 'Other' },
    statuses: {
      draft: 'Draft',
      pending: 'Pending',
      returned: 'Returned',
      approved: 'Approved',
      transferred: 'Transferred',
      received: 'Completed',
      cancelled: 'Cancelled',
    },
    columns: {
      ctmNo: 'Ref. No.',
      date: 'Date',
      port: 'Port',
      currency: 'Currency',
      amount: 'Amount',
      purpose: 'Purpose',
      status: 'Status',
    },
    steps: {
      created: 'Request Created',
      review: 'Office Review',
      arranged: 'Payment Arranged',
      received: 'Cash Received',
      posted: 'Posted to Cash Account',
    },
    stepStates: {
      done: 'Completed',
      current: 'Pending',
      waiting: 'Waiting',
      returned: 'Returned',
      cancelled: 'Cancelled',
    },
    sentOf: '{{sent}} sent of {{requested}} requested',
    stepDone: {
      review: 'Approved',
      arranged: 'Transferred',
    },
    by: 'by {{name}}',
    via: 'via {{text}}',
    postedNote: 'Automatically after confirmation',
    actions: {
      new: 'New Request',
      saveDraft: 'Save Draft',
      submit: 'Submit Request',
      confirmReceipt: 'Confirm Receipt',
      cancel: 'Cancel Request',
      approve: 'Approve',
      return: 'Return',
      arrange: 'Arrange Payment',
    },
    summary: {
      requested: 'Total Requested',
      approved: 'Total Approved',
      received: 'Total Received',
      pending: 'Pending',
    },
    statusFilter: 'Status',
    empty: 'No CTM request yet.',
    emptyQueue: 'Nothing waiting here.',
    selectHint: 'Select a request in the list to see its workflow.',
    criticalRule:
      'Requesting or approving does not change the cash balance. Only the master’s receipt confirmation posts the cash to the account.',
    returnedNote: 'Returned by the office: {{reason}}',
    cancelledNote: 'Cancelled: {{reason}}',
    lockedNote: 'Submitted requests cannot be changed; the office returns them for correction.',
  },
  receipt: {
    title: 'Confirm Receipt – {{no}}',
    text: 'Enter the cash actually received. The amount is posted to the {{currency}} cash account as “CTM Received”.',
    expected: 'Expected: {{amount}}',
    amount: 'Amount received',
    day: 'Date received',
    time: 'Time',
    receiptNo: 'Receipt number',
    remarks: 'Remarks',
    reasonHint: 'Required when the amount differs from the expected amount.',
    confirm: 'Confirm Receipt',
  },
  accounts: {
    cardLabel: '{{currency}} cash account',
    states: { open: 'Open', closed: 'Closed', none: 'Not in cash box' },
    inTransit: 'In transit (CTM)',
    inTransitShort: '+{{amount}} in transit',
    expected: 'After delivery',
    close: 'Close account',
    closeBlocked: 'Only an empty account with nothing in transit can be closed.',
    closeTitle: 'Close {{currency}} account',
    closeText:
      'The vessel can no longer receive new cash in this currency. The account and its history stay; it can be opened again.',
    closeReason: 'Reason',
    reopen: 'Open again',
    addToBox: 'Add to cash box',
    setup: 'Set up cash box',
    setupTitle: 'Set up cash box',
    add: 'Add currency',
    addTitle: 'Add currency',
    setupText:
      'Choose the currencies of the vessel cash box. Each currency is a separate account; the master keeps the cash on board.',
    currencies: 'Currencies',
    other: 'Other currency',
    otherHint: 'ISO 4217 code, e.g. NOK',
    otherInvalid: 'Enter a three-letter code.',
    notConfiguredOffice:
      'The cash box of this vessel is not set up yet; the vessel can use any currency. Set it up to fix the currencies.',
    notConfiguredShip:
      'The office has not set up the cash box of this vessel yet; any currency can be used.',
    rule: 'Approved and sent CTM cash is shown as in transit; it enters the balance only when the master confirms receipt.',
    box: 'Cash box',
    boxReady: 'Set up',
    boxMissing: 'Not set up',
    fleetTotal: 'Fleet total',
    noConversion:
      'Totals are per currency; currencies are not converted into each other (no exchange rate source in the system).',
  },
  decision: {
    approveSendText:
      'The master requested {{amount}}. Enter the amount the office sends: it can be less, equal or more; a difference needs a reason and is recorded.',
    approveSend: 'Approve and send',
    sendAmount: 'Amount to send',
    requested: 'Requested',
    sending: 'Sending',
    difference: 'Difference',
    varianceReason: 'Reason for the difference',
    partialHint: 'Partial payment: the rest is not sent; the master can raise a new request.',
    overHint: 'More than requested: the extra amount is recorded with the reason.',
    approveTitle: 'Approve {{no}}',
    returnTitle: 'Return {{no}}',
    approveText:
      'Approve the request of {{amount}}. The cash balance changes only when the master confirms receipt.',
    returnText: 'Return the request to the master for correction.',
    reason: 'Reason',
    note: 'Note (optional)',
  },
  transfer: {
    title: 'Arrange Payment – {{no}}',
    amount: 'Amount',
    method: 'Payment / delivery',
    arrangedOn: 'Arranged on',
    reference: 'Agent / bank reference',
    remarks: 'Remarks',
    save: 'Save',
  },
  cancel: {
    title: 'Cancel {{no}}',
    text: 'The request is cancelled with a reason and stays in the history.',
    reason: 'Reason',
    confirm: 'Cancel Request',
  },
  txn: {
    columns: {
      dateTime: 'Date / Time',
      id: 'ID',
      type: 'Type',
      description: 'Description',
      category: 'Category',
      in: 'In ({{currency}})',
      out: 'Out ({{currency}})',
      balance: 'Balance ({{currency}})',
      receipt: 'Receipt',
      action: 'Action',
    },
    types: {
      opening: 'Opening',
      receipt: 'Receipt',
      ctm_received: 'CTM Received',
      expense: 'Expense',
      exchange_out: 'Exchange Out',
      exchange_in: 'Exchange In',
      reversal: 'Reversal',
      adjustment: 'Adjustment',
    },
    categories: {
      crew_cash_advance: 'Crew Cash Advance',
      crew_wages: 'Crew Wages',
      port_expenses: 'Port Expenses',
      provisions: 'Provisions',
      bonded_stores: 'Bonded Stores',
      medical_expenses: 'Medical Expenses',
      transportation: 'Transportation',
      repairs: 'Repairs',
      emergency_expenses: 'Emergency Expenses',
      other: 'Other',
      other_income: 'Other Income',
      refund: 'Refund',
      crew_repayment: 'Crew Repayment',
      ctm: 'Cash to Master',
      exchange: 'Currency Exchange',
      cash_difference: 'Cash Difference',
    },
    descriptions: {
      opening: 'Opening Balance',
      ctm_received: 'From Office',
      reversal: 'Reverses {{no}}',
      adjustment: 'Count difference',
    },
    new: 'New Transaction',
    newExpense: 'Expense (cash out)',
    newReceipt: 'Receipt (cash in)',
    newOpening: 'Opening balance',
    newExchange: 'Currency exchange',
    fromPayroll: 'From Payroll',
    reverse: 'Reverse',
    reversedBy: 'Reversed by {{no}}',
    rate: 'Rate {{rate}}',
    linked: 'Linked: {{ref}}',
    enteredBy: 'Entered by {{name}}',
    approvedBy: 'Approved by {{name}}',
    empty: 'No transaction in this period.',
    noMatch: 'No transaction matches the search.',
    more: 'More actions for {{no}}',
    pageInfo: '{{from}}–{{to}} of {{total}}',
  },
  entry: {
    title: {
      opening: 'Opening Balance',
      receipt: 'Cash Receipt',
      expense: 'Cash Expense',
    },
    day: 'Date',
    time: 'Time',
    amount: 'Amount',
    currency: 'Currency',
    category: 'Category',
    payee: 'Payee / Received From',
    port: 'Vessel / Port',
    receiptNo: 'Receipt Number',
    remarks: 'Remarks',
    linkedType: 'Linked record',
    linkedRef: 'Reference No.',
    none: 'None',
    payrollNote:
      'Crew cash advances and cash wages are posted from Payroll (“From Payroll”); they cannot be entered by hand.',
    openingNote: 'The opening balance is entered once per currency account.',
    save: 'Save',
    linkTypes: {
      purchase: 'Purchase',
      provisions: 'Provisions',
      port_expenses: 'Port Expenses',
      defect: 'Defect / PMS',
      voyage: 'Voyage',
      document: 'Document',
      other: 'Other',
    },
  },
  exchange: {
    title: 'Currency Exchange',
    text: 'Records a linked cash out and cash in between two currency accounts with the applied rate.',
    from: 'From account',
    fromAmount: 'Amount given',
    to: 'To account',
    toAmount: 'Amount received',
    rate: 'Applied rate: 1 {{from}} = {{rate}} {{to}}',
    payee: 'Exchanged with',
  },
  // Makbuz ve talep belgesi dosyaları (ADR 0121 §2; sorular #966)
  files: {
    open: 'Receipt files of {{no}}',
    title: 'Receipt Files · {{no}}',
    text: 'Scanned receipts, invoices and request documents of the transaction. Files are added on board and cannot be changed; the transaction itself stays as entered.',
    empty: 'No receipt file yet.',
    add: 'Add receipt file',
  },
  reverse: {
    title: 'Reverse {{no}}',
    text: 'Transactions are never changed or deleted. A reversal entry with the opposite amount is posted today; the original stays in the cash book.',
    reason: 'Reason',
    confirm: 'Reverse',
  },
  balance: {
    opening: 'Opening',
    in: 'In',
    out: 'Out',
    balance: 'Balance',
    closed: 'Month closed',
  },
  statement: {
    opening: 'Opening Balance',
    received: 'Total Cash Received',
    paid: 'Total Cash Paid',
    closing: 'Calculated Closing Balance',
    counted: 'Actual Cash Counted',
    difference: 'Difference',
    status: 'Status',
    masterApproval: 'Master Approval',
    officeApproval: 'Office Approval',
    balanced: 'Balanced',
    notBalanced: 'Difference',
    open: 'Open',
    signed: 'Signed',
    view: 'View / Print',
    save: 'Save PDF',
    ledger: 'Transactions',
    printed: 'Printed {{at}}',
  },
  reconcile: {
    kinds: { month_end: 'Month-end count', handover: 'Cash Handover', takeover: 'Cash Takeover' },
    title: {
      month_end: 'Close {{month}} – {{currency}}',
      handover: 'Cash Handover – {{currency}}',
      takeover: 'Cash Takeover – {{currency}}',
    },
    closeMonth: 'Close Month',
    handover: 'Cash Handover',
    takeover: 'Take Over',
    counted: 'Actual cash counted',
    reason: 'Explanation of the difference',
    reasonHint: 'Required when the count differs; the difference is posted as an adjustment.',
    remarks: 'Remarks',
    handoverTo: 'Incoming master',
    lockNote:
      'After closing, no transaction can be entered in this month for this currency. Corrections are made in a later month with a reversal.',
    handoverNote:
      'The outgoing master counts and hands over the cash; the incoming master confirms with their own count.',
    confirm: 'Sign',
    month: 'Month',
    currency: 'Currency',
    status: 'Status',
    closed: 'Closed',
    open: 'Open',
    due: 'Ready to close',
    columns: {
      kind: 'Record',
      currency: 'Currency',
      period: 'Month / Date',
      calculated: 'Calculated',
      counted: 'Counted',
      difference: 'Difference',
      signedBy: 'Signed by',
      office: 'Office',
    },
    review: { approved: 'Approved', queried: 'Queried', none: 'Awaiting' },
    reviewAction: 'Review',
    reviewTitle: 'Office review – {{kind}} {{period}}',
    reviewDecision: 'Decision',
    reviewRemarks: 'Remarks',
    empty: 'No count recorded yet.',
    takeoverOf: 'Takeover of {{name}}’s handover',
  },
  reports: {
    ledger: {
      title: 'Cash book (CSV)',
      text: 'All transactions of the selected account and month with running balance.',
    },
    ctm: { title: 'CTM register (CSV)', text: 'Every CTM request with transfer and receipt.' },
    statement: {
      title: 'Monthly Cash Statement (PDF)',
      text: 'The statement of the selected account and month with signature boxes.',
    },
    download: 'Download',
  },
  payroll: {
    title: 'Post from Payroll',
    text: 'Cash advances and cash wages recorded in Payroll that are not yet in the cash account. Each item is posted once.',
    empty: 'Nothing to post: every cash payroll item is already in the cash account.',
    post: 'Post',
    kinds: { entry: 'Cash advance', payment: 'Cash wages' },
  },
  notices: {
    accountsOpened: 'Cash accounts opened: {{list}}.',
    accountClosed: '{{currency}} account closed.',
    saved: 'Request {{no}} saved as draft.',
    submitted: 'Request submitted to the office.',
    cancelled: 'Request cancelled.',
    received: 'Receipt confirmed and posted to the cash account.',
    approved: 'Request approved.',
    returned: 'Request returned to the master.',
    transferred: 'Payment arranged.',
    entry: 'Transaction recorded.',
    exchange: 'Exchange recorded.',
    reversed: 'Reversal posted.',
    reconciled: 'Count signed.',
    reviewed: 'Review saved.',
    posted: 'Payroll item posted to the cash account.',
    file: '{{name}} saved.',
  },
  empty: {
    noAccess: 'You do not have permission to see the cash account.',
    noAccount: 'No cash account yet. Enter an opening balance or confirm a CTM receipt.',
  },
  errors: {
    load: 'The cash account could not be loaded.',
    save: 'Could not save ({{code}}).',
    codes: {
      CASH_ACCOUNT_OPEN: 'This currency account is already open.',
      CASH_ACCOUNT_NOT_OPEN: 'This currency account is not open.',
      CASH_ACCOUNT_NOT_EMPTY: 'The account still has cash or cash in transit.',
      CASH_ACCOUNT_CLOSED: 'The office closed this currency account.',
      CASH_ACCOUNT_MISSING: 'This currency is not in the vessel cash box.',
      CTM_NOT_FINAL_STEP: 'The amount is entered at the last approval step.',
      VARIANCE_REASON_REQUIRED: 'Explain why the amount differs from the request.',
      FORBIDDEN: 'You do not have permission for this.',
      VALIDATION_FAILED: 'Check the highlighted fields.',
      INSUFFICIENT_CASH: 'There is not enough cash in this account.',
      MONTH_CLOSED: 'This month is closed; enter the correction in an open month.',
      MONTH_NOT_ENDED: 'The month can be closed on its last day or after.',
      PREVIOUS_MONTH_OPEN: 'Close the previous month first.',
      NO_CASH_ACCOUNT: 'There is no transaction in this account.',
      OPENING_EXISTS: 'This account already has an opening balance.',
      DUPLICATE_LINK: 'This item is already in the cash account.',
      PAYROLL_LINK_REQUIRED: 'Crew advances and wages are posted from Payroll.',
      ALREADY_REVERSED: 'This transaction is already reversed.',
      CANNOT_REVERSE_REVERSAL: 'A reversal cannot be reversed.',
      CTM_NOT_APPROVED: 'The office has not approved this request yet.',
      CTM_ALREADY_RECEIVED: 'Receipt of this request is already confirmed.',
      CTM_ALREADY_TRANSFERRED: 'The payment is already arranged.',
      CTM_LOCKED: 'Submitted requests cannot be changed.',
      CTM_CANCELLED: 'The request is cancelled.',
      REQUIRED_DATE_PAST: 'The required date cannot be in the past.',
      RECEIPT_REASON_REQUIRED: 'Explain why the amount differs.',
      DIFFERENCE_REASON_REQUIRED: 'Explain the difference.',
      FUTURE_DATE: 'The date cannot be in the future.',
      CONTENT_CHANGED: 'The request changed meanwhile; reload and try again.',
      STALE_DECISION: 'The request changed meanwhile; reload and try again.',
      NOT_APPROVER: 'Your role is not the approver of this step.',
      SELF_APPROVAL_FORBIDDEN: 'You cannot decide on your own request.',
      REAUTHENTICATION_REQUIRED: 'Enter your password to confirm.',
      SAME_CURRENCY: 'Choose two different currencies.',
      CASH_LINKED: 'This payroll item is in the cash account; reverse the cash transaction first.',
      TAKEOVER_NOT_ADDRESSEE: 'Only the incoming master can take over this handover.',
      INVALID_HANDOVER: 'Choose the incoming master.',
      ROW_VERSION_CONFLICT: 'The record changed meanwhile; reload and try again.',
    },
  },
};

export const cashTr: typeof cashEn = {
  title: 'Finans',
  sections: {
    accounts: {
      title: 'Kasalar',
      description:
        'Gemilerin kasaları: her para birimi kendi bakiyesiyle ayrı bir hesaptır. Kasayı ofis kurar, gemi yönetir.',
    },
    'ctm-requests': {
      title: 'CTM Talepleri',
      description:
        'Kaptana nakit (Cash to Master): kaptan şirketten nakit ister; ofis inceler, onaylar ve ödemeyi düzenler; kaptan teslim aldığını teyit eder.',
    },
    'ctm-approvals': {
      title: 'CTM Onay ve Transfer',
      description: 'Ofis kararı ya da ödeme / acente teslimi düzenlenmeyi bekleyen talepler.',
    },
    'ctm-receipts': {
      title: 'CTM Teslim Teyidi',
      description:
        'Kaptanın fiilen teslim aldığı nakdi teyit etmesini bekleyen onaylı talepler. Kasaya yalnız teyit kaydedilir.',
    },
    'ctm-history': {
      title: 'CTM Geçmişi',
      description: 'Geminin bütün CTM talepleri ve akışları.',
    },
    transactions: {
      title: 'Kasa İşlemleri',
      description:
        'Gemi kasa defteri: her nakit giriş ve çıkışı ayrı işlemdir; her para birimi ayrı kasadır.',
    },
    balances: {
      title: 'Para Birimine Göre Bakiye',
      description: 'Her para birimi kasasının açılışı, girişi, çıkışı ve bakiyesi.',
    },
    reconciliation: {
      title: 'Kasa Mutabakatı',
      description:
        'Ay sonunda ya da kaptan değişiminde fiziki nakit sayılır ve kasa defteriyle karşılaştırılır; ay sonra kilitlenir.',
    },
    monthly: {
      title: 'Aylık Kasa Raporu',
      description: 'Bir para birimi kasasının aylık dökümü, basıma hazır.',
    },
    reports: {
      title: 'Finans Raporları',
      description: 'Kasa defteri, CTM kaydı ve aylık döküm çıktıları.',
    },
  },
  vessel: 'Gemi',
  month: 'Dönem',
  account: 'Kasa',
  accountName: 'Kaptan Kasası - {{currency}}',
  search: 'İşlem ara…',
  all: 'Tümü',
  closeWindow: 'Pencereyi kapat',
  cancelAction: 'Vazgeç',
  tableOf: '{{name}} tablosu',
  password: 'Parola',
  readOnlyOffice:
    'Kasayı gemide kaptan tutar. Ofiste salt okunurdur; ofis CTM taleplerine karar verir, ödemeyi düzenler ve sayımları inceler.',
  panels: {
    accounts: 'Kasalar',
    vesselAccounts: 'Kasa – {{vessel}}',
    fleet: 'Filo Kasa Görünümü',
    request: 'CTM Talebi',
    status: 'CTM Onay ve Durum',
    list: 'CTM Talep Listesi',
    summary: 'CTM Özeti',
    transactions: 'Kasa Hesabı – İşlemler',
    balances: 'Para Birimine Göre Bakiye',
    statement: 'Aylık Kasa Dökümü ({{currency}}) – {{month}}',
    reconciliation: 'Ay Sonu Durumu',
    records: 'Sayımlar, Devir ve Devralma',
    reports: 'Finans Raporları',
  },
  tabs: { workflow: 'Akış', history: 'Geçmiş' },
  ctm: {
    fields: {
      vessel: 'Gemi',
      ctmNo: 'CTM Referans No.',
      requestDate: 'Talep Tarihi',
      requiredDate: 'İhtiyaç Tarihi',
      port: 'Liman / Ülke',
      currency: 'Para Birimi',
      requestedAmount: 'Talep Edilen Tutar',
      cashBalance: 'Mevcut Kasa',
      estimatedExpenses: 'Tahmini Harcama',
      purpose: 'Gerekçe',
      deliveryMethod: 'Teslim Yöntemi',
      remarks: 'Açıklama / Gerekçe',
      requestedBy: 'Talep Eden',
      status: 'Onay Durumu',
      auto: 'Kaydedince verilir',
      remarksPlaceholder: 'Nakit ihtiyacını ve gerekçesini yazın…',
    },
    purposes: {
      crew_cash_advance: 'Mürettebat Avansı',
      crew_wages: 'Mürettebat Maaşı',
      port_expenses: 'Liman Masrafları',
      provisions: 'Kumanya',
      bonded_stores: 'Gümrüksüz Mağaza',
      medical_expenses: 'Sağlık Masrafları',
      transportation: 'Ulaşım',
      repairs: 'Tamir',
      emergency_expenses: 'Acil Masraflar',
      other: 'Diğer',
    },
    methods: { agent: 'Acente', bank: 'Banka', other: 'Diğer' },
    statuses: {
      draft: 'Taslak',
      pending: 'Beklemede',
      returned: 'İade',
      approved: 'Onaylandı',
      transferred: 'Gönderildi',
      received: 'Tamamlandı',
      cancelled: 'İptal',
    },
    columns: {
      ctmNo: 'Ref. No.',
      date: 'Tarih',
      port: 'Liman',
      currency: 'Para Birimi',
      amount: 'Tutar',
      purpose: 'Gerekçe',
      status: 'Durum',
    },
    steps: {
      created: 'Talep Oluşturuldu',
      review: 'Ofis İncelemesi',
      arranged: 'Ödeme Düzenlendi',
      received: 'Nakit Teslim Alındı',
      posted: 'Kasaya İşlendi',
    },
    stepStates: {
      done: 'Tamamlandı',
      current: 'Beklemede',
      waiting: 'Sırada',
      returned: 'İade',
      cancelled: 'İptal',
    },
    sentOf: '{{requested}} istendi, {{sent}} gönderildi',
    stepDone: {
      review: 'Onaylandı',
      arranged: 'Gönderildi',
    },
    by: '{{name}}',
    via: '{{text}} ile',
    postedNote: 'Teyitten sonra kendiliğinden',
    actions: {
      new: 'Yeni Talep',
      saveDraft: 'Taslağı Kaydet',
      submit: 'Talebi Gönder',
      confirmReceipt: 'Teslimi Teyit Et',
      cancel: 'Talebi İptal Et',
      approve: 'Onayla',
      return: 'İade Et',
      arrange: 'Ödemeyi Düzenle',
    },
    summary: {
      requested: 'Toplam Talep',
      approved: 'Toplam Onay',
      received: 'Toplam Teslim',
      pending: 'Bekleyen',
    },
    statusFilter: 'Durum',
    empty: 'Henüz CTM talebi yok.',
    emptyQueue: 'Burada bekleyen yok.',
    selectHint: 'Akışını görmek için listeden bir talep seçin.',
    criticalRule:
      'Talep ya da onay kasa bakiyesini değiştirmez. Nakit kasaya yalnız kaptanın teslim teyidiyle girer.',
    returnedNote: 'Ofis iade etti: {{reason}}',
    cancelledNote: 'İptal edildi: {{reason}}',
    lockedNote: 'Gönderilmiş talep değiştirilemez; ofis düzeltme için iade eder.',
  },
  receipt: {
    title: 'Teslimi Teyit Et – {{no}}',
    text: 'Fiilen teslim alınan nakdi girin. Tutar {{currency}} kasasına “CTM Received” olarak işlenir.',
    expected: 'Beklenen: {{amount}}',
    amount: 'Teslim alınan tutar',
    day: 'Teslim tarihi',
    time: 'Saat',
    receiptNo: 'Makbuz numarası',
    remarks: 'Açıklama',
    reasonHint: 'Tutar beklenenden farklıysa zorunlu.',
    confirm: 'Teslimi Teyit Et',
  },
  accounts: {
    cardLabel: '{{currency}} kasası',
    states: { open: 'Açık', closed: 'Kapalı', none: 'Kasa dışı' },
    inTransit: 'Yolda (CTM)',
    inTransitShort: '+{{amount}} yolda',
    expected: 'Teslimden sonra',
    close: 'Kasayı kapat',
    closeBlocked: 'Yalnız boş ve yolda parası olmayan kasa kapanır.',
    closeTitle: '{{currency}} kasasını kapat',
    closeText: 'Gemi bu birimde yeni nakit alamaz. Kasa ve geçmişi kalır; yeniden açılabilir.',
    closeReason: 'Gerekçe',
    reopen: 'Yeniden aç',
    addToBox: 'Kasaya ekle',
    setup: 'Kasayı kur',
    setupTitle: 'Kasayı kur',
    add: 'Birim ekle',
    addTitle: 'Birim ekle',
    setupText:
      'Gemi kasasının para birimlerini seçin. Her birim ayrı bir hesaptır; nakdi gemide kaptan tutar.',
    currencies: 'Para birimleri',
    other: 'Başka birim',
    otherHint: 'ISO 4217 kodu, ör. NOK',
    otherInvalid: 'Üç harfli kod girin.',
    notConfiguredOffice:
      'Bu geminin kasası henüz kurulmadı; gemi her birimi kullanabilir. Birimleri belirlemek için kasayı kurun.',
    notConfiguredShip: 'Ofis bu geminin kasasını henüz kurmadı; her birim kullanılabilir.',
    rule: 'Onaylanan ve gönderilen CTM nakdi yolda görünür; bakiyeye yalnız kaptan teslimi teyit edince girer.',
    box: 'Kasa',
    boxReady: 'Kurulu',
    boxMissing: 'Kurulmadı',
    fleetTotal: 'Filo toplamı',
    noConversion:
      'Toplamlar para birimi başınadır; birimler birbirine çevrilmez (sistemde kur kaynağı yok).',
  },
  decision: {
    approveSendText:
      'Kaptan {{amount}} istedi. Ofisin göndereceği tutarı girin: az, eşit ya da fazla olabilir; fark gerekçeyle kayda geçer.',
    approveSend: 'Onayla ve gönder',
    sendAmount: 'Gönderilecek tutar',
    requested: 'İstenen',
    sending: 'Gönderilen',
    difference: 'Fark',
    varianceReason: 'Fark gerekçesi',
    partialHint: 'Kısmi ödeme: kalan gönderilmez; kaptan yeni talep açabilir.',
    overHint: 'İstenenden fazla: fazlası gerekçesiyle kayda geçer.',
    approveTitle: '{{no}} onayı',
    returnTitle: '{{no}} iadesi',
    approveText:
      '{{amount}} tutarındaki talebi onaylayın. Kasa bakiyesi yalnız kaptan teslimi teyit edince değişir.',
    returnText: 'Talebi düzeltme için kaptana iade edin.',
    reason: 'Gerekçe',
    note: 'Not (isteğe bağlı)',
  },
  transfer: {
    title: 'Ödemeyi Düzenle – {{no}}',
    amount: 'Tutar',
    method: 'Ödeme / teslim',
    arrangedOn: 'Düzenleme tarihi',
    reference: 'Acente / banka referansı',
    remarks: 'Açıklama',
    save: 'Kaydet',
  },
  cancel: {
    title: '{{no}} iptali',
    text: 'Talep gerekçeyle iptal edilir ve geçmişte kalır.',
    reason: 'Gerekçe',
    confirm: 'Talebi İptal Et',
  },
  txn: {
    columns: {
      dateTime: 'Tarih / Saat',
      id: 'No',
      type: 'Tür',
      description: 'Açıklama',
      category: 'Kategori',
      in: 'Giriş ({{currency}})',
      out: 'Çıkış ({{currency}})',
      balance: 'Bakiye ({{currency}})',
      receipt: 'Makbuz',
      action: 'İşlem',
    },
    types: {
      opening: 'Açılış',
      receipt: 'Giriş',
      ctm_received: 'CTM Teslim',
      expense: 'Gider',
      exchange_out: 'Bozdurma Çıkış',
      exchange_in: 'Bozdurma Giriş',
      reversal: 'Ters Kayıt',
      adjustment: 'Düzeltme',
    },
    categories: {
      crew_cash_advance: 'Mürettebat Avansı',
      crew_wages: 'Mürettebat Maaşı',
      port_expenses: 'Liman Masrafları',
      provisions: 'Kumanya',
      bonded_stores: 'Gümrüksüz Mağaza',
      medical_expenses: 'Sağlık Masrafları',
      transportation: 'Ulaşım',
      repairs: 'Tamir',
      emergency_expenses: 'Acil Masraflar',
      other: 'Diğer',
      other_income: 'Diğer Gelir',
      refund: 'İade',
      crew_repayment: 'Mürettebat Geri Ödemesi',
      ctm: 'Kaptana Nakit',
      exchange: 'Döviz Bozdurma',
      cash_difference: 'Kasa Farkı',
    },
    descriptions: {
      opening: 'Açılış Bakiyesi',
      ctm_received: 'Ofisten',
      reversal: '{{no}} ters kaydı',
      adjustment: 'Sayım farkı',
    },
    new: 'Yeni İşlem',
    newExpense: 'Gider (çıkış)',
    newReceipt: 'Giriş',
    newOpening: 'Açılış bakiyesi',
    newExchange: 'Döviz bozdurma',
    fromPayroll: 'Payroll’dan',
    reverse: 'Ters kayıt',
    reversedBy: '{{no}} ile ters çevrildi',
    rate: 'Kur {{rate}}',
    linked: 'Bağ: {{ref}}',
    enteredBy: 'Giren: {{name}}',
    approvedBy: 'Onaylayan: {{name}}',
    empty: 'Bu dönemde işlem yok.',
    noMatch: 'Aramaya uyan işlem yok.',
    more: '{{no}} için diğer işlemler',
    pageInfo: '{{from}}–{{to}} / {{total}}',
  },
  entry: {
    title: {
      opening: 'Açılış Bakiyesi',
      receipt: 'Nakit Giriş',
      expense: 'Nakit Gider',
    },
    day: 'Tarih',
    time: 'Saat',
    amount: 'Tutar',
    currency: 'Para Birimi',
    category: 'Kategori',
    payee: 'Ödenen / Alınan Kişi',
    port: 'Gemi / Liman',
    receiptNo: 'Makbuz Numarası',
    remarks: 'Açıklama',
    linkedType: 'Bağlı kayıt',
    linkedRef: 'Referans No.',
    none: 'Yok',
    payrollNote:
      'Mürettebat avansı ve nakit maaş Payroll’dan işlenir (“Payroll’dan”); elle girilemez.',
    openingNote: 'Açılış bakiyesi her para birimi kasasına bir kez girilir.',
    save: 'Kaydet',
    linkTypes: {
      purchase: 'Satın Alma',
      provisions: 'Kumanya',
      port_expenses: 'Liman Masrafları',
      defect: 'Arıza / PMS',
      voyage: 'Sefer',
      document: 'Belge',
      other: 'Diğer',
    },
  },
  exchange: {
    title: 'Döviz Bozdurma',
    text: 'İki kasa arasında bağlı bir çıkış ve giriş, uygulanan kurla kaydedilir.',
    from: 'Çıkan kasa',
    fromAmount: 'Verilen tutar',
    to: 'Giren kasa',
    toAmount: 'Alınan tutar',
    rate: 'Uygulanan kur: 1 {{from}} = {{rate}} {{to}}',
    payee: 'Bozdurulan yer',
  },
  files: {
    open: '{{no}} makbuz dosyaları',
    title: 'Makbuz Dosyaları · {{no}}',
    text: 'İşlemin taranmış makbuz, fatura ve talep belgeleri. Dosyalar gemide eklenir ve değiştirilmez; işlemin kendisi girildiği gibi kalır.',
    empty: 'Henüz makbuz dosyası yok.',
    add: 'Makbuz dosyası ekle',
  },
  reverse: {
    title: '{{no}} ters kaydı',
    text: 'İşlemler değiştirilmez ve silinmez. Ters tutarlı bir kayıt bugünün tarihiyle girilir; asıl kayıt defterde kalır.',
    reason: 'Gerekçe',
    confirm: 'Ters Kayıt',
  },
  balance: {
    opening: 'Açılış',
    in: 'Giriş',
    out: 'Çıkış',
    balance: 'Bakiye',
    closed: 'Ay kapalı',
  },
  statement: {
    opening: 'Açılış Bakiyesi',
    received: 'Toplam Giriş',
    paid: 'Toplam Çıkış',
    closing: 'Hesaplanan Kapanış',
    counted: 'Sayılan Nakit',
    difference: 'Fark',
    status: 'Durum',
    masterApproval: 'Kaptan Onayı',
    officeApproval: 'Ofis Onayı',
    balanced: 'Denk',
    notBalanced: 'Fark var',
    open: 'Açık',
    signed: 'İmzalandı',
    view: 'Görüntüle / Bas',
    save: 'PDF Kaydet',
    ledger: 'İşlemler',
    printed: 'Basım {{at}}',
  },
  reconcile: {
    kinds: { month_end: 'Ay sonu sayımı', handover: 'Kasa Devri', takeover: 'Kasa Devralma' },
    title: {
      month_end: '{{month}} kapanışı – {{currency}}',
      handover: 'Kasa Devri – {{currency}}',
      takeover: 'Kasa Devralma – {{currency}}',
    },
    closeMonth: 'Ayı Kapat',
    handover: 'Kasayı Devret',
    takeover: 'Devral',
    counted: 'Sayılan nakit',
    reason: 'Farkın açıklaması',
    reasonHint: 'Sayım farklıysa zorunlu; fark düzeltme kaydıyla işlenir.',
    remarks: 'Açıklama',
    handoverTo: 'Gelen kaptan',
    lockNote:
      'Kapanıştan sonra bu para biriminde bu aya işlem girilemez. Düzeltme sonraki ayda ters kayıtla yapılır.',
    handoverNote: 'Giden kaptan nakdi sayar ve devreder; gelen kaptan kendi sayımıyla devralır.',
    confirm: 'İmzala',
    month: 'Ay',
    currency: 'Para Birimi',
    status: 'Durum',
    closed: 'Kapalı',
    open: 'Açık',
    due: 'Kapanabilir',
    columns: {
      kind: 'Kayıt',
      currency: 'Para Birimi',
      period: 'Ay / Tarih',
      calculated: 'Hesaplanan',
      counted: 'Sayılan',
      difference: 'Fark',
      signedBy: 'İmzalayan',
      office: 'Ofis',
    },
    review: { approved: 'Onaylandı', queried: 'Soru var', none: 'Bekliyor' },
    reviewAction: 'İncele',
    reviewTitle: 'Ofis incelemesi – {{kind}} {{period}}',
    reviewDecision: 'Karar',
    reviewRemarks: 'Açıklama',
    empty: 'Henüz sayım yok.',
    takeoverOf: '{{name}} devrinin devralınması',
  },
  reports: {
    ledger: {
      title: 'Kasa defteri (CSV)',
      text: 'Seçili kasa ve ayın bütün işlemleri, yürüyen bakiyeyle.',
    },
    ctm: { title: 'CTM kaydı (CSV)', text: 'Bütün CTM talepleri, transfer ve teslimle.' },
    statement: {
      title: 'Aylık Kasa Dökümü (PDF)',
      text: 'Seçili kasa ve ayın dökümü, imza kutularıyla.',
    },
    download: 'İndir',
  },
  payroll: {
    title: 'Payroll’dan İşle',
    text: 'Payroll’da kayıtlı, henüz kasaya işlenmemiş nakit avans ve nakit maaşlar. Her kalem bir kez işlenir.',
    empty: 'İşlenecek kalem yok: bütün nakit kalemler kasada.',
    post: 'İşle',
    kinds: { entry: 'Nakit avans', payment: 'Nakit maaş' },
  },
  notices: {
    accountsOpened: 'Kasalar açıldı: {{list}}.',
    accountClosed: '{{currency}} kasası kapatıldı.',
    saved: '{{no}} taslak olarak kaydedildi.',
    submitted: 'Talep ofise gönderildi.',
    cancelled: 'Talep iptal edildi.',
    received: 'Teslim teyit edildi ve kasaya işlendi.',
    approved: 'Talep onaylandı.',
    returned: 'Talep kaptana iade edildi.',
    transferred: 'Ödeme düzenlendi.',
    entry: 'İşlem kaydedildi.',
    exchange: 'Bozdurma kaydedildi.',
    reversed: 'Ters kayıt girildi.',
    reconciled: 'Sayım imzalandı.',
    reviewed: 'İnceleme kaydedildi.',
    posted: 'Payroll kalemi kasaya işlendi.',
    file: '{{name}} kaydedildi.',
  },
  empty: {
    noAccess: 'Kasayı görme yetkiniz yok.',
    noAccount: 'Henüz kasa yok. Açılış bakiyesi girin ya da bir CTM teslimini teyit edin.',
  },
  errors: {
    load: 'Kasa yüklenemedi.',
    save: 'Kaydedilemedi ({{code}}).',
    codes: {
      CASH_ACCOUNT_OPEN: 'Bu birimin kasası zaten açık.',
      CASH_ACCOUNT_NOT_OPEN: 'Bu birimin kasası açık değil.',
      CASH_ACCOUNT_NOT_EMPTY: 'Kasada nakit ya da yolda para var.',
      CASH_ACCOUNT_CLOSED: 'Ofis bu birimin kasasını kapattı.',
      CASH_ACCOUNT_MISSING: 'Bu birim gemi kasasında yok.',
      CTM_NOT_FINAL_STEP: 'Tutar son onay adımında girilir.',
      VARIANCE_REASON_REQUIRED: 'Tutarın talepten neden farklı olduğunu yazın.',
      FORBIDDEN: 'Bu işlem için yetkiniz yok.',
      VALIDATION_FAILED: 'İşaretli alanları kontrol edin.',
      INSUFFICIENT_CASH: 'Bu kasada yeterli nakit yok.',
      MONTH_CLOSED: 'Bu ay kapalı; düzeltmeyi açık bir aya girin.',
      MONTH_NOT_ENDED: 'Ay, son günü ya da sonrasında kapanır.',
      PREVIOUS_MONTH_OPEN: 'Önce önceki ayı kapatın.',
      NO_CASH_ACCOUNT: 'Bu kasada işlem yok.',
      OPENING_EXISTS: 'Bu kasanın açılış bakiyesi zaten var.',
      DUPLICATE_LINK: 'Bu kalem zaten kasada.',
      PAYROLL_LINK_REQUIRED: 'Mürettebat avansı ve maaşı Payroll’dan işlenir.',
      ALREADY_REVERSED: 'Bu işlem zaten ters çevrildi.',
      CANNOT_REVERSE_REVERSAL: 'Ters kayıt ters çevrilemez.',
      CTM_NOT_APPROVED: 'Ofis bu talebi henüz onaylamadı.',
      CTM_ALREADY_RECEIVED: 'Bu talebin teslimi zaten teyit edildi.',
      CTM_ALREADY_TRANSFERRED: 'Ödeme zaten düzenlendi.',
      CTM_LOCKED: 'Gönderilmiş talep değiştirilemez.',
      CTM_CANCELLED: 'Talep iptal edildi.',
      REQUIRED_DATE_PAST: 'İhtiyaç tarihi geçmişte olamaz.',
      RECEIPT_REASON_REQUIRED: 'Tutarın neden farklı olduğunu yazın.',
      DIFFERENCE_REASON_REQUIRED: 'Farkı açıklayın.',
      FUTURE_DATE: 'Tarih gelecekte olamaz.',
      CONTENT_CHANGED: 'Talep bu arada değişti; yenileyip tekrar deneyin.',
      STALE_DECISION: 'Talep bu arada değişti; yenileyip tekrar deneyin.',
      NOT_APPROVER: 'Rolünüz bu adımın onaylayıcısı değil.',
      SELF_APPROVAL_FORBIDDEN: 'Kendi talebinize karar veremezsiniz.',
      REAUTHENTICATION_REQUIRED: 'Onaylamak için parolanızı girin.',
      SAME_CURRENCY: 'İki farklı para birimi seçin.',
      CASH_LINKED: 'Bu Payroll kalemi kasada; önce kasa kaydını ters çevirin.',
      TAKEOVER_NOT_ADDRESSEE: 'Bu devri yalnız gelen kaptan devralabilir.',
      INVALID_HANDOVER: 'Gelen kaptanı seçin.',
      ROW_VERSION_CONFLICT: 'Kayıt bu arada değişti; yenileyip tekrar deneyin.',
    },
  },
};
